import { useParams, useNavigate } from "react-router-dom";
import { cases } from "@/data/cases";
import { casesTranslations } from "@/data/casesTranslations";
import { useLanguage } from "@/contexts/LanguageContext";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { ArrowLeft, ExternalLink, Briefcase, Target, Lightbulb, CheckCircle2 } from "lucide-react";

const CaseDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  
  const baseCaseStudy = cases.find(c => c.slug === slug);
  const translatedCase = casesTranslations[language].cases.find(c => c.slug === slug);
  
  const caseStudy = baseCaseStudy && translatedCase ? {
    ...baseCaseStudy,
    ...translatedCase,
    details: {
      ...baseCaseStudy.details,
      ...translatedCase.details,
      figmaLink: baseCaseStudy.details.figmaLink,
      prototypeLink: baseCaseStudy.details.prototypeLink,
      images: baseCaseStudy.details.images.map((img, i) => ({
        ...img,
        caption: translatedCase.details.images[i].caption
      }))
    }
  } : null;
  
  if (!caseStudy) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">{t("caseDetail.notFound")}</h1>
          <Button onClick={() => navigate("/")} variant="outline">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t("caseDetail.backToHome")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-32 gradient-hero">
        <div className="container mx-auto px-6">
          <Button
            onClick={() => navigate("/")}
            variant="ghost"
            className="mb-8 hover:bg-primary/10"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t("caseDetail.back")}
          </Button>
          
          <div className="max-w-4xl">
            <div className="flex flex-wrap gap-2 mb-4">
              {caseStudy.tags.map((tag, i) => (
                <Badge key={i} variant="secondary" className="bg-primary/10 text-primary">
                  {tag}
                </Badge>
              ))}
            </div>
            
            <h1 className="text-5xl font-bold mb-4">{caseStudy.title}</h1>
            
            <div className="flex flex-wrap gap-6 text-muted-foreground mb-6">
              <div className="flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-primary" />
                <span>{caseStudy.company}</span>
              </div>
              <div className="flex items-center gap-2">
                <Target className="h-5 w-5 text-primary" />
                <span>{caseStudy.role}</span>
              </div>
            </div>
            
            <p className="text-xl text-muted-foreground leading-relaxed">
              {caseStudy.description}
            </p>
            
            <div className="flex gap-4 mt-8">
              {caseStudy.details.figmaLink && (
                <Button
                  onClick={() => window.open(caseStudy.details.figmaLink, "_blank")}
                  className="gradient-accent shadow-glow"
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  {t("caseDetail.viewFigma")}
                </Button>
              )}
              {caseStudy.details.prototypeLink && (
                <Button
                  onClick={() => window.open(caseStudy.details.prototypeLink, "_blank")}
                  variant="outline"
                  className="border-primary/40 hover:border-primary hover:shadow-glow"
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  {t("caseDetail.viewPrototype")}
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Main Image */}
      <section className="py-12 bg-secondary/30">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <img
              src={caseStudy.image}
              alt={caseStudy.title}
              className="w-full rounded-lg shadow-premium"
            />
          </div>
        </div>
      </section>

      {/* Challenge Section */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
              <Target className="h-8 w-8 text-primary" />
              {t("caseDetail.challenge")}
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              {caseStudy.details.challenge}
            </p>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-16 bg-secondary/30">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
              <Lightbulb className="h-8 w-8 text-primary" />
              {t("caseDetail.solution")}
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8">
              {caseStudy.details.solution}
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="p-6 gradient-premium border-primary/20">
                <h3 className="text-xl font-bold mb-4">{t("caseDetail.responsibilities")}</h3>
                <ul className="space-y-2">
                  {caseStudy.details.responsibilities.map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>
              
              <Card className="p-6 gradient-premium border-primary/20">
                <h3 className="text-xl font-bold mb-4">{t("caseDetail.tools")}</h3>
                <div className="flex flex-wrap gap-2">
                  {caseStudy.details.tools.map((tool, i) => (
                    <Badge key={i} variant="secondary" className="bg-primary/10 text-primary">
                      {tool}
                    </Badge>
                  ))}
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Images Gallery */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold mb-12 text-center">{t("caseDetail.projectEvolution")}</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {caseStudy.details.images.map((image, i) => (
                <div key={i} className="animate-fade-in" style={{ animationDelay: `${i * 0.1}s` }}>
                  <Card className="overflow-hidden gradient-premium border-primary/20">
                    <img
                      src={image.src}
                      alt={image.caption}
                      className="w-full h-auto"
                    />
                    <div className="p-4">
                      <p className="text-sm text-muted-foreground">{image.caption}</p>
                    </div>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-16 bg-secondary/30">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8">{t("caseDetail.results")}</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {caseStudy.details.results.map((result, i) => (
                <Card
                  key={i}
                  className="p-6 gradient-premium border-primary/20 hover:shadow-accent transition-all"
                >
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary mt-1 flex-shrink-0" />
                    <p className="text-muted-foreground">{result}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Key Learning */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <Card className="p-8 gradient-premium border-l-4 border-l-primary">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
                <Lightbulb className="h-6 w-6 text-primary" />
                {t("caseDetail.keyLearning")}
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                {caseStudy.details.keyLearning}
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-secondary/30">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">{t("caseDetail.cta.title")}</h2>
          <p className="text-muted-foreground mb-8">{t("caseDetail.cta.description")}</p>
          <Button
            onClick={() => navigate("/#contact")}
            size="lg"
            className="gradient-accent shadow-glow"
          >
            {t("caseDetail.cta.button")}
          </Button>
        </div>
      </section>
    </div>
    <Footer />
    </>
  );
};

export default CaseDetail;
